import { useMemo, useRef, useState } from "react";
import { getStroke } from 'perfect-freehand';
import { GeneralVideoPlayer } from "../../../components/others/GeneralVideoPlayer"
import { getSvgPathFromStroke } from "../../../services/shorts/shorts-service";
import { ActionButton } from "../../../components/buttons/ActionButton";
import { patchData } from "../../../services/api/api-tools";
import { ArrowDownTrayIcon, CheckCircleIcon, PaintBrushIcon } from "@heroicons/react/20/solid";
import { downloadVideoFromUrl } from "../../../helpers/file/file-helper";
import { useAlert } from "../../../contexts/AlertContext";
import { formatSecondsToFullDateTime } from "../../../helpers/date/date-helper";

const strokeOptions = {
  size: 12,
  smoothing: 0.5,
  thinning: 0.5,
  streamline: 0.5,
  easing: (t) => t,
  // start: {
  //   taper: 0,
  //   cap: true,
  // },
  // end: {
  //   taper: 0,
  //   cap: true,
  // },
};

const strokeColors = [
  '#db2c23',
  '#edd21f',
  '#64d15c',
  '#369feb',
  '#a259f0',
  '#f52ab4'
];

const ShortsVideoBlockContent = ({ selectedRecord, recordAnnotations, setRecordAnnotations, currentTimeRef, videoRect, duration, viewOnly, setViewOnly }) => {
  const { showAlert } = useAlert();

  const [isEditing, setIsEditing] = useState(false);
  const [currentAnnotation, setCurrentAnnotation] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const svgRef = useRef(null);
  const [strokeColor, setStrokeColor] = useState(strokeColors[0]);

  /* Strokes helpers */
  const getPointInVideo = (e) => {
    const svgElement = svgRef.current;
    if (!svgElement) return null;

    const rect = svgElement.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    if (x < 0 || y < 0 || x > rect.width || y > rect.height) return null;

    return [
      x / rect.width,
      y / rect.height
    ];
  };

  const createAnnotation = (stroke) => {
    const start = currentTimeRef.current;

    return {
      stroke,
      stroke_color: strokeColor,
      start,
      end: Math.min(start + 10, duration)
    };
  };

  const handlePointerDown = (e) => {
    e.stopPropagation();
    if (!svgRef.current) return;
    svgRef.current.setPointerCapture(e.pointerId);
    const point = getPointInVideo(e);
    if (!point) return;
    setIsDrawing(true);
    setCurrentAnnotation(createAnnotation([point]));
  };

  const handlePointerMove = (e) => {
    e.stopPropagation();
    if (!isDrawing || e.buttons !== 1) return;
    const point = getPointInVideo(e);
    if (!point) return;
    setCurrentAnnotation((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        stroke: [...prev.stroke, point]
      };
    });
  };

  const handlePointerUp = (e) => {
    e.stopPropagation();
    setIsDrawing(false);
  };

  /* Other functions */
  const handleAddAnnotation = () => {
    setIsEditing(true);
  };

  const handleCancelAnnotation = () => {
    setCurrentAnnotation(null);
    setIsDrawing(false);
    setIsEditing(false);
  };

  const handleSaveAnnotation = async () => {
    if (currentAnnotation?.stroke.length > 0) {
      const newRecordAnnotations = [
        ...(recordAnnotations ?? []),
        currentAnnotation
      ];

      const savedAnnotations = await patchData(
        `records/${selectedRecord.id}/annotations`,
        newRecordAnnotations
      );

      setRecordAnnotations(savedAnnotations);
    }

    setCurrentAnnotation(null);
    setIsDrawing(false);
    setIsEditing(false);
  };

  const handleExport = async () => {
    try {
      showAlert('success', 'La vidéo est en cours d\'exportation. Le téléchargement démarrera automatiquement.');

      const response = await fetch(process.env.REACT_APP_ADDRESS + 'annotate-video', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          video_id: selectedRecord.id + '_external-1',
          annotations: recordAnnotations.map((annotation) => ({
            points: annotation.stroke,
            start: annotation.start,
            end: annotation.end,
            color: annotation.stroke_color,
            args: strokeOptions,
          })),
        }),
      });

      const data = await response.json();
      console.log(data);

      const videoUrl = `${process.env.REACT_APP_ADDRESS}public/media/annotated_shorts/${selectedRecord.id}_external-1.mp4`;
      downloadVideoFromUrl(videoUrl);
    } catch (error) {
      console.error('Error while requesting data.', error);
      alert('Error while requesting data.');
    }
  };

  const annotationPaths = useMemo(() => {
    if (!videoRect) return [];

    return [...recordAnnotations, currentAnnotation].filter(Boolean).map((annotation) => {
      if (annotation.stroke.length === 0) return null;

      const isCurrentAnnotation = annotation === currentAnnotation;

      const points = annotation.stroke.map(([x, y]) => [
        x * videoRect.width,
        y * videoRect.height
      ]);

      const stroke = getStroke(points, strokeOptions);
      const pathData = getSvgPathFromStroke(stroke);

      return {
        ...annotation,
        pathData,
        isCurrentAnnotation
      };
    }).filter(Boolean);
  }, [recordAnnotations, currentAnnotation, videoRect, strokeColor]);

  const visibleAnnotationPaths = annotationPaths.filter((annotation) => {
    const currentTime = currentTimeRef.current;
    return annotation.isCurrentAnnotation || currentTime >= annotation.start && currentTime <= annotation.end;
  });

  return (
    <div className="h-full w-full relative">
      <div className="z-30 absolute top-4 left-4">
        <span className="text-nowrap rounded-md backdrop-blur-md bg-white/20 px-2 py-1 text-xs font-medium text-white inline-block">
          {formatSecondsToFullDateTime(selectedRecord.date)}
        </span>
      </div>
      <div className="z-30 absolute top-2 right-2 p-2 rounded-md bg-gray-500/20 backdrop-blur-lg" onClick={(e) => e.stopPropagation()}>
        {viewOnly ?
          <div className="flex gap-2">
            <ActionButton text="Editer" Icon={PaintBrushIcon} size="sm" onClick={() => setViewOnly(false)} />
            <ActionButton text="Exporter" Icon={ArrowDownTrayIcon} size="sm" onClick={handleExport} />
          </div>
        : isEditing ?
          <div className="flex gap-6 items-center">
            {/* Colors */}
            <div className="flex gap-2">
              {strokeColors.map((color) => (
                <div
                  key={color}
                  className={`size-4 rounded-full border-2 ${color === strokeColor ? 'border-white' : 'border-transparent'} select-none cursor-pointer`}
                  style={{ background: color }}
                  onClick={() => setStrokeColor(color)}
                />
              ))}
            </div>

            {/* Buttons*/}
            <div className="flex gap-2">
              <ActionButton text="Enregistrer l'annotation" size="sm" onClick={handleSaveAnnotation} />
              <ActionButton text="Annuler" size="sm" onClick={handleCancelAnnotation} color="danger" />
            </div>
          </div>
        :
          <div className="flex gap-2">
            <ActionButton text="Ajouter une annotation" Icon={PaintBrushIcon} size="sm" onClick={handleAddAnnotation} />
            <ActionButton text="Terminer l'édition" Icon={CheckCircleIcon} size="sm" onClick={() => setViewOnly(true)} />
          </div>
        }
      </div>

      <svg
        ref={svgRef}
        className="absolute"
        style={{
          left: videoRect?.left ?? 0,
          top: videoRect?.top ?? 0,
          width: videoRect?.width ?? '100%',
          height: videoRect?.height ?? '100%',
          touchAction: 'none',
          pointerEvents: isEditing ? 'auto' : 'none'
        }}
        onPointerDown={(e) => isEditing && handlePointerDown(e)}
        onPointerMove={(e) => isEditing && handlePointerMove(e)}
        onPointerUp={(e) => isEditing && handlePointerUp(e)}
        onClick={(e) => isEditing && e.stopPropagation()}
      >
        {visibleAnnotationPaths.map((annotation, index) => (
          <path
            key={annotation.id ?? `current-${index}`}
            d={annotation.pathData}
            fill={annotation.stroke_color ?? 'black'}
          />
        ))}
      </svg>
    </div>
  );
};


export const ShortsVideoBlock = ({ selectedRecord, recordAnnotations, setRecordAnnotations, setVideoDuration, setVideoCurrentTime, viewOnly, setViewOnly }) => {

  // const src = 'https://res.cloudinary.com/db0ea7ydl/video/upload/v1772704855/samples/sea-turtle.mp4';
  const src = `${process.env.REACT_APP_ADDRESS}public/media/shorts/${selectedRecord.id}_external-1.mp4`;

  return (
    <div className={`${viewOnly ? 'h-full' : 'h-[60%]'} transition-all flex justify-center items-center`}>
      <GeneralVideoPlayer
        videoUrl={src}
        handleOnProgress={(progress) => setVideoCurrentTime(progress.playedSeconds)}
        handleVideoLoadedWithDuration={setVideoDuration}
        startPlaying={false}
        startProgress={0}
        command={null}
        OverlayComponent={ShortsVideoBlockContent}
        overlayProps={{
          selectedRecord,
          recordAnnotations,
          setRecordAnnotations,
          viewOnly,
          setViewOnly,
        }}
      />
    </div>
  );
};
